import html2canvas from 'html2canvas';
import { DateTime } from 'luxon';
import { AvailableTemplate, PowerpointExportDto, PowerpointSlideType } from '@ecosystem/export-api';
import { ComponentHeading, DataColumn, ExportOptions, PageDsl, Params, SectionType, TabbedSection } from '../../types';
import { getExportContext } from '..';
import { getComponentTitle } from './componentToPptData';
import { getExportHeadingTitle } from './pptUtils';

type PptImage = { dataUrl: string; width: number; height: number };

export const getPageTitle = (dsl: PageDsl): string | null => {
  const label = dsl?.heading?.title;
  return Array.isArray(label) ? label.map(l => (typeof l === 'string' ? l : (l.displayText ?? ''))).join(' ') : label;
};
export const getTabTitle = (dsl: PageDsl): string | null => {
  const tabSection = dsl.sections.find(section => section.type === SectionType.tabbed) as TabbedSection;
  const tabTitle = tabSection ? tabSection.tabs.find(tab => tab.name === tabSection.activeTab)?.label : '';
  return tabTitle;
};

const getComponentOrPageTitle = (
  dsl: PageDsl,
  componentSettings?: Params,
  allColumns?: DataColumn[],
  componentHeadingTitle?: ComponentHeading['title']
) => {
  if (componentHeadingTitle && allColumns?.length && componentSettings) {
    return getComponentTitle(componentHeadingTitle, componentSettings, allColumns);
  }
  return getPageTitle(dsl);
};

const createSlideTitle = (
  dsl: PageDsl,
  componentSettings?: Params,
  allColumns?: DataColumn[],
  componentHeadingTitle?: ComponentHeading['title']
) => {
  const title = getComponentOrPageTitle(dsl, componentSettings, allColumns, componentHeadingTitle);
  const tabTitle = getTabTitle(dsl) ?? '';
  return [title, tabTitle].filter(Boolean).join(' - ');
};

const chartToPptData = async (
  { fileHeaderSettings, fileName, contentName }: ExportOptions,
  dsl: PageDsl,
  chartLabel: string | null,
  exportContentRef: HTMLElement[] | HTMLElement,
  componentSettings?: Params,
  allColumns?: DataColumn[],
  componentHeadingTitle?: ComponentHeading['title']
): Promise<PowerpointExportDto> => {
  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];
  const clonedComponentHeadingTitle = getExportHeadingTitle(componentHeadingTitle, sharedBrands);
  let slideTitle = createSlideTitle(dsl, componentSettings, allColumns, clonedComponentHeadingTitle);
  let slideSubtitle = chartLabel || contentName || '';
  if (slideTitle.length > 50 && slideTitle.includes('by')) {
    const splitIndex = slideTitle.indexOf('by');
    if (splitIndex > 30) {
      slideSubtitle = slideTitle.substring(splitIndex).trim();
      slideTitle = slideTitle.substring(0, splitIndex).trim();
    }
  }
  const pptSlides = [];

  const elementRefs = Array.isArray(exportContentRef) ? exportContentRef : [exportContentRef];
  for (const elementRef of elementRefs) {
    const canvas = await html2canvas(elementRef, {
      windowWidth: elementRef.scrollWidth + 100,
    });

    const dataUrl = canvas.toDataURL('image/png');

    pptSlides.push({
      type: PowerpointSlideType.Image,
      image: { dataUrl: dataUrl, width: elementRef.scrollWidth, height: elementRef.scrollHeight },
      title: slideTitle,
      subtitle: slideSubtitle,
      context: getExportContext(fileHeaderSettings),
    });
  }

  return {
    name: fileName,
    userDateTimeIso8601: DateTime.local().toISO(),
    template: AvailableTemplate.Powerpoint,
    slides: pptSlides,
  };
};

const chartToPptDataForCollections = async (
  exportContentRef: HTMLElement[] | HTMLElement
): Promise<{ images: PptImage[] }> => {
  const pptSlides: PptImage[] = [];

  const elementRefs = Array.isArray(exportContentRef) ? exportContentRef : [exportContentRef];
  for (const elementRef of elementRefs) {
    const canvas = await html2canvas(elementRef, {
      windowWidth: elementRef.scrollWidth + 100,
    });

    const dataUrl = canvas.toDataURL('image/png');

    pptSlides.push({ dataUrl, width: elementRef.scrollWidth, height: elementRef.scrollHeight });
  }

  return {
    images: pptSlides,
  };
};

export { chartToPptData, chartToPptDataForCollections };
